import { Link } from 'react-router-dom';
import type { ArticleData } from '../data';

interface ArticleLayoutProps {
  article: ArticleData;
}

export default function ArticleLayout({ article }: ArticleLayoutProps) {
  return (
    <article className="pt-6 pb-16 md:pt-8 md:pb-20">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <nav className="mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <li><Link to="/" className="hover:text-primary-500 transition-colors duration-200">Home</Link></li>
            <li className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-800 font-medium truncate">{article.title}</li>
          </ol>
        </nav>

        <header className="mb-10">
          <div className="inline-flex items-center gap-2 bg-accent-100 rounded-full px-4 py-1.5 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
            <span className="text-xs font-semibold text-accent-700 uppercase tracking-wide">Comprehensive Guide</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
            {article.title}
          </h1>
          <p className="text-base text-foreground-600 leading-relaxed">
            {article.description}
          </p>
          <div className="flex items-center gap-4 mt-6 pt-6 border-t border-background-200">
            <span className="text-xs text-foreground-500">Updated: August 2026</span>
            <span className="text-xs text-foreground-500">{article.readTime}</span>
          </div>
        </header>

        <div className="relative w-full h-[360px] md:h-[440px] rounded-2xl overflow-hidden mb-12">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-center"
          />
        </div>

        <div className="space-y-10 text-foreground-800 leading-relaxed">
          {article.sections.map((section, idx) => (
            <section key={idx}>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">{section.heading}</h2>
              {section.paragraphs.map((p, pIdx) => (
                <p key={pIdx} className="text-base text-foreground-700 mb-4 last:mb-0">{p}</p>
              ))}
            </section>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-background-200">
          <div className="bg-background-100 border border-background-200/80 rounded-2xl p-6 md:p-8 text-center">
            <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">Explore the Basetao Spreadsheet Today</h3>
            <p className="text-sm text-foreground-600 mb-6 max-w-lg mx-auto">
              Browse hundreds of curated product listings with QC photos, pricing comparisons, and shipping estimates — all in one organized document.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto bg-primary-500 text-white font-semibold text-sm px-8 py-3 rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
              >
                Basetao Spreadsheet
              </a>
              <Link
                to="/"
                className="w-full sm:w-auto bg-background-200 text-foreground-800 font-medium text-sm px-8 py-3 rounded-full hover:bg-background-300 transition-all duration-300 whitespace-nowrap"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}